import React from 'react';

import * as Sentry from '@sentry/browser';
// @mui
import { Container, Grid, Typography } from '@mui/material';
// hooks
import useSettings from 'src/hooks/useSettings';
// components
import Page from 'src/components/Page';
import PageHeader from 'src/components/PageHeader';
import ReloadButton from 'src/components/ReloadButton';
import ReportTimeframeInput, {
  dateRangeToTimeframe,
  TimeframeDateRange,
  timeframes,
  timeframeToDateRange,
} from 'src/components/ReportTimeframeInput';
// sections
import { DashboardGreeting, DashboardStatistics } from 'src/sections/dashboard';
// queries
import useCallStatistics from 'src/hooks/core-api/useCallStatistics';
import useCurrentUser from 'src/hooks/core-api/useCurrentUser';
// store
import useMainStore from 'src/store';
// types
import useLocalStorage from 'src/hooks/useLocalStorage';
import { DateRange } from 'src/types';
//

// ----------------------------------------------------------------------

const defaultTimeframe = timeframes[0];
const defaultDateRange = timeframeToDateRange(defaultTimeframe);

const DashboardHomePage = () => {
  const [displayDashboardGreeting, setDisplayDashboardGreeting] = useLocalStorage(
    'displayDashboardGreeting',
    true
  );
  const [dateRange, setDateRange] = React.useState<DateRange>(defaultDateRange);
  const { themeStretch } = useSettings();
  const currentUserQuery = useCurrentUser();

  const selectedLocations = useMainStore((state) => state.selectedLocations);

  const callStatisticsQuery = useCallStatistics({
    request: {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      locationIds: selectedLocations?.map((location) => location.id) ?? [],
    },
    analytics: {
      timeframe: dateRangeToTimeframe(dateRange).label,
    },
  });
  const { first_name } = currentUserQuery?.data ?? {};

  const DashboardStatisticsSection = () => {
    if (callStatisticsQuery.isError) {
      Sentry.captureException(callStatisticsQuery.error);
      return (
        <Grid item xs={12}>
          <Typography>Error loading report data</Typography>
        </Grid>
      );
    }

    if (
      callStatisticsQuery.isLoading ||
      callStatisticsQuery.isFetching ||
      callStatisticsQuery.data === undefined
    ) {
      return <></>;
    }

    return (
      <>
        <DashboardStatistics statistics={callStatisticsQuery.data} />
      </>
    );
  };

  const handleReportRefresh = () => {
    callStatisticsQuery.refetch();
  };

  const handleTimeframeDateRangeChange = (tdr: TimeframeDateRange) => {
    console.log('Timeframe and Date range changed', tdr);
    setDateRange(tdr.dateRange);
  };

  return (
    <Page title="Dashboard">
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <Grid container spacing={3}>
          {displayDashboardGreeting && (
            <>
              <Grid item xs={12} md={8} sx={{ mt: 1 }}>
                <DashboardGreeting
                  displayName={first_name}
                  onClose={() => setDisplayDashboardGreeting(false)}
                />
              </Grid>
              <Grid item xs={0} md={4} />
            </>
          )}

          <Grid item xs={12} md={12}>
            <PageHeader
              title="Reports Overview"
              button={
                <ReloadButton
                  tooltip="Refresh report"
                  isFetching={callStatisticsQuery.isLoading || callStatisticsQuery.isFetching}
                  onRefetch={handleReportRefresh}
                />
              }
            />
          </Grid>

          <Grid item xs={12} />

          <Grid item xs={12} md={9}>
            <ReportTimeframeInput
              onChange={handleTimeframeDateRangeChange}
              defaultTimeframe={defaultTimeframe}
            />
          </Grid>

          <Grid item xs={12} />

          <DashboardStatisticsSection />
        </Grid>
      </Container>
    </Page>
  );
};

export default DashboardHomePage;
